import { Address } from '@adornis/address/db/address.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { config } from '@ils/config/config.js';
import type { Location } from '@ils/locations/db/location.js';
import { EmailAttachments, EmailType, EmailVars } from '@ils/mails/emailEnums.js';
import { ExamDayRatingType, ExamType } from '@ils/static/enums.js';
import { brandDictionary } from '@ils/translation/translation.js';
import { ILSFile } from '@legacy-db/ils-files.js';
import { getSalutation } from '@legacy-db/utils.js';
import { html } from 'lit';
import { DateTime } from 'luxon';

const scheduleLink = (examDayID: string) =>
  `<a href="${config.get('ROOT_URL')}start/scheduling?day=${examDayID}" target="_blank">zum Stundenplan</a>`;

const makeDateTime = (date: Date | DateTime) => {
  if (DateTime.isDateTime(date)) return date;
  return DateTime.fromJSDate(date);
};

const getResultMailData = (
  examDay: { start: Date | DateTime; examType: Maybe<ExamType> },
  trainee: { ecommerce?: Maybe<{ firstName: string; lastName: string }>; username: string },
  resultPDF: { name: string; _id: string },
) => ({
  data: {
    [EmailVars.DATE]: makeDateTime(examDay.start).toFormat('dd.MM.yyyy'),
    [EmailVars.LASTNAME]: trainee.ecommerce?.lastName ?? trainee.username,
    [EmailVars.SALUTATION]: getSalutation(trainee),
  },
  attachments: {
    [EmailAttachments.RESULT]: ILSFile.getServeLink(resultPDF._id) + '/' + resultPDF.name,
    [EmailAttachments.INFO_PDF]: ILSFile.getDownloadLink('info_famed') + '/info_famed.pdf',
  },
  scope: examDay.examType ?? ExamType.FAMED,
});

export interface ExamInfoMailUserData {
  _id: string;
  _class: string;
  ecommerce?: Maybe<{ firstName: string; lastName: string }>;
  profile?: Maybe<{ gender: string }>;
  email: string;
}

export const getExamInfoMailData = (
  user: ExamInfoMailUserData,
  examDay: { _id: string; start: Date; ratingType?: Maybe<string>; examType: ExamType },
  duration: { start: Date; end: Date },
  location?: Maybe<EntityData<Location>>,
) => {
  return {
    data: {
      [EmailVars.DATE]: DateTime.fromJSDate(examDay.start).toFormat('dd.MM.yyyy'),
      [EmailVars.FIRSTNAME]: user.ecommerce?.firstName,
      [EmailVars.LASTNAME]: user.ecommerce?.lastName,
      [EmailVars.SALUTATION]: getSalutation(user),
      [EmailVars.START_TIME]: DateTime.fromJSDate(duration.start).toFormat('HH:mm'),
      [EmailVars.END_TIME]: DateTime.fromJSDate(duration.end).toFormat('HH:mm'),
      [EmailVars.TIME_OF_ARIVAL]: DateTime.fromJSDate(duration.start).minus({ minutes: 60 }).toFormat('HH:mm'),
      [EmailVars.EXAMDAY_TYPE]: brandDictionary.ratingType[examDay.ratingType ?? ExamDayRatingType.FAMED].long[0],
      [EmailVars.SCHEDULE_LINK]: scheduleLink(examDay._id),
      [EmailVars.ADDRESS]: location?.address ? new Address(location.address).toString().replace(',', '\n') : '---',
    },
    attachments: {
      [EmailAttachments.INFO_PDF]: ILSFile.getDownloadLink('info_famed') + '/info_famed.pdf',
    },
    scope: examDay.examType,
  };
};

export const ResultPassedMail = defineMailTemplate(EmailType.RESULT_PASSED, {
  displayName: EmailType.RESULT_PASSED,
  defaultTemplate: {
    subject: `Ergebnis Ihrer Fachsprachenprüfung Medizin vom {{${EmailVars.DATE}}}`,
    content: `
<p>Sehr geehrte/r {{${EmailVars.SALUTATION}}} {{${EmailVars.LASTNAME}}},</p>
<p>wir haben Ihre Fachsprachenprüfung vom {{${EmailVars.DATE}}} bewertet und informieren Sie mit dieser Email über Ihr Ergebnis.</p>
<p>Im Anhang finden Sie eine Übersicht des von Ihnen erzielten Gesamtergebnisses inklusive einer Aufstellung Ihrer Leistung in den einzelnen Prüfungsteilen.</p>
<p>Ihre Urkunde zum Nachweis der bestandenen Prüfung schicken wir Ihnen im Laufe der kommenden Woche per Post zu.</p>
<p><b>WICHTIG:</b> Sowohl Ihr Name als auch Geburtsdatum und Geburtsort werden auf der Urkunde aufgeführt. Teilen Sie uns daher bitte folgende Daten noch einmal mit:</p>
<p>Name, Vorname:<br>Geburtsort, Geburtsland:<br>Geburtsdatum:</p>
<p>Teilen Sie uns bitte außerdem Ihre Adresse mit, an die wir die Urkunde senden sollen.</p>
<p>Mit freundlichen Grüßen</p>
<p>Juliane Blessin i.A. des FaMed-Teams</p>
`,
    footer: '',
  },
  variables: [EmailVars.LASTNAME, EmailVars.DATE, EmailVars.SALUTATION],
  attachments: [EmailAttachments.RESULT, EmailAttachments.INFO_PDF],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Prüfling<br />
      - Techsupport<br />
      - Accounting
    </p>
    <p>
      Wann:<br />
      - per Admin über die Nutzerverwaltung jeweils pro Prüfling<br />
      - per Knopf in Bewertungsübersicht an alle Prüflinge welche zu diesem Zeitpunkt bereits als "Bestanden" gelten
    </p>
  `,
  getMailData: getResultMailData,
});

export const ResultFailedMail = defineMailTemplate(EmailType.RESULT_FAILED, {
  displayName: EmailType.RESULT_FAILED,
  defaultTemplate: {
    subject: `Ergebnis Ihrer Fachsprachenprüfung Medizin vom {{${EmailVars.DATE}}}`,
    content: `
<p>Sehr geehrte/r {{${EmailVars.SALUTATION}}} {{${EmailVars.LASTNAME}}},</p>
<p>hiermit teilen wir Ihnen das Ergebnis Ihrer Fachsprachenprüfung Medizin vom {{${EmailVars.DATE}}} mit.</p>
<p>Leider reicht Ihr Sprachniveau noch nicht an das C1-Niveau des Gemeinsamen Europäischen Referenzrahmens (GER) heran.</p>
<p>Wir bieten Ihnen gerne ein kostenloses Nachgespräch zu Ihrer Prüfung an, in welchem wir auf die zu verbessernden Bereiche eingehen und Ihnen Hinweise zur Vorbereitung auf Ihre nächste Prüfung geben.</p>
<p>Geben Sie uns bitte Rückmeldung, ob Sie an einem solchen Feedback-Gespräch interessiert sind, dann teilen wir Ihnen mögliche Termine mit.</p>
<p>Im Anhang finden Sie das Gesamtergebnis inklusive einer Übersicht über Ihre Ergebnisse in den einzelnen Prüfungsteilen.</p>
`,
    footer: '',
  },
  variables: [EmailVars.LASTNAME, EmailVars.DATE, EmailVars.SALUTATION],
  attachments: [EmailAttachments.RESULT, EmailAttachments.INFO_PDF],
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Prüfling<br />
      - Techsupport<br />
      - Accounting
    </p>
    <p>
      Wann:<br />
      - per Admin über die Nutzerverwaltung jeweils pro Prüfling<br />
      - per Knopf in Bewertungsübersicht an alle Prüflinge welche zu diesem Zeitpunkt bereits als "Nicht bestanden"
      gelten
    </p>
  `,
  getMailData: getResultMailData,
});

const examInfoMailDefaults = {
  defaultTemplate: {
    subject: 'Deine Prüfung wurde geplant',
    content: `<p> Hallo {{${EmailVars.SALUTATION}}} {{${EmailVars.LASTNAME}}}, </p><p> bitte seien Sie pünktlich am {{${EmailVars.DATE}}} um {{${EmailVars.TIME_OF_ARIVAL}}} in Prüfungszentrum. </p> <p> Ihr FaMed Team </p>`,
    footer: '',
  },
  variables: [
    EmailVars.FIRSTNAME,
    EmailVars.LASTNAME,
    EmailVars.SALUTATION,
    EmailVars.DATE,
    EmailVars.START_TIME,
    EmailVars.END_TIME,
    EmailVars.TIME_OF_ARIVAL,
    EmailVars.SCHEDULE_LINK,
    EmailVars.ADDRESS,
    EmailVars.EXAMDAY_TYPE,
  ],
  attachments: [EmailAttachments.INFO_PDF],
  getMailData: getExamInfoMailData,
};

export const ExamInfoCandidateMail = defineMailTemplate(EmailType.EXAM_INFO_CANDIDATE, {
  displayName: EmailType.EXAM_INFO_CANDIDATE,
  ...examInfoMailDefaults,
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Prüfling
    </p>
    <p>
      Wann:<br />
      - per Knopf im Stundenplan des Examtermin
    </p>
  `,
});

export const ExamInfoExamCenterMail = defineMailTemplate(EmailType.EXAM_INFO_EXAMCENTER, {
  displayName: EmailType.EXAM_INFO_EXAMCENTER,
  ...examInfoMailDefaults,
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Prüfungszentrum<br />
      - Prüfungsleiter
    </p>
    <p>
      Wann:<br />
      - per Knopf im Stundenplan des Examtermin
    </p>
  `,
});

export const ExamInfoTesterMail = defineMailTemplate(EmailType.EXAM_INFO_TESTER, {
  displayName: EmailType.EXAM_INFO_TESTER,
  ...examInfoMailDefaults,
  editorHint: () => html`
    <p>
      Wird gesendet an:<br />
      - Schauspieler
    </p>
    <p>
      Wann:<br />
      - per Knopf im Stundenplan des Examtermin
    </p>
  `,
});
